import { Box, Divider, Table as CTable, Thead, Tbody, Heading, Checkbox } from '@chakra-ui/react';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { EmptyTableRow } from 'components/Table';
import ReactTablePagination from 'components/Table/ReactTablePagination/ReactTablePagination';
import EditActionButton from 'containers/Reports/shared/EditActionButton';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableDataCell, ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { roles } from 'global/rolesAndPermissions';
import { useUserObject } from 'store/useUserStore';
import { formatDistanceUnit, formatYearMonthDayTime, formatCurrency } from 'utils/format';
import { convertSecondsToMinutesRoundDown, isDistanceInMiles } from 'utils/helpers';

interface IReportsByTaskTableProps {
  data: any;
  dateRange: any[];
  checkedTimesheet: string[];
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, timeSheetId: string) => void;
  children?: React.ReactNode | undefined;
}

const initialReportsByTaskTableQuery = {
  current_page: 1,
  first_page_url: '',
  from: 0,
  last_page: 0,
  last_page_url: '',
  next_page_url: '',
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
};

const ReportsByTaskTable: React.FC<IReportsByTaskTableProps> = ({
  children,
  data,
  dateRange,
  checkedTimesheet,
  handleCheckboxChange,
}) => {
  const { t } = useTranslation(['reports', 'common']);

  const userStore = useUserObject();

  // custom pagination starts //
  const [paginationQuery, setPaginationQuery] =
    useState<typeof initialReportsByTaskTableQuery>(initialReportsByTaskTableQuery);

  useStickyHeader({ tableData: data, paginationQuery: paginationQuery });

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatePaginationQuery = (previousSearchQuery: typeof paginationQuery) => ({
      ...previousSearchQuery,
      current_page: selectedPageIndex + 1,
    });
    setPaginationQuery(updatePaginationQuery);
  }, []);

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationQuery((previousSearchQuery: typeof paginationQuery) => ({
      ...previousSearchQuery,
      current_page: 1,
      per_page: pageSize,
    }));
  };

  const formattedData: any[] =
    Array.isArray(data?.users) &&
    data.users
      .map((user: any) => {
        return [
          ...(Array.isArray(user?.data?.timesheets) && user.data?.timesheets.map((timesheet: any) => timesheet)),
          { ...user?.data?.total, isTotal: true },
        ];
      })
      .flat();

  const getPaginatedData = useCallback(() => {
    let firstIndex = 0;
    let secondIndex: number = formattedData.length;

    let totalCounters = 0;

    // If the next item from the last item of a page is the total then display it too
    if (formattedData[paginationQuery.current_page * +paginationQuery.per_page]?.isTotal) {
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page + 1;
    } else if (formattedData[paginationQuery.current_page * +paginationQuery.per_page - 1]?.isTotal) {
      // If the first item of a page is the total then don't display it
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page;
    } else {
      firstIndex = (paginationQuery.current_page - 1) * +paginationQuery.per_page;
      secondIndex = paginationQuery.current_page * +paginationQuery.per_page;
    }

    // Count how many totals are there in the interval and increment the interval accordingly to render out a correct number of items
    for (let i = 0; i < secondIndex; i++) {
      if (formattedData[i]?.isTotal) {
        totalCounters++;
      }
    }

    return formattedData.slice(firstIndex, secondIndex + totalCounters);
  }, [paginationQuery, formattedData]);

  const currentItems = getPaginatedData() || [];

  // custom pagination ends //

  const getTimesheetIds = useCallback(() => {
    let timesheetIds = [];

    if (Array.isArray(data?.users) && data.users.length) {
      timesheetIds = data?.users
        ?.map((user: any) => {
          return Array.isArray(user?.data?.timesheets) && user.data.timesheets.length
            ? user?.data?.timesheets?.map((timesheet: any) => timesheet?.id)
            : [];
        })
        .flat();
    }

    return timesheetIds;
  }, [data]);

  return (
    <div>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {t('reports:searchResults')}
      </Heading>
      {data?.attributes ? (
        <ExportMenu
          timesheetIds={getTimesheetIds()}
          dateRange={dateRange}
          reportType="byTask"
          attributes={data?.attributes}
          displaySettings={data?.integrations}
        />
      ) : null}
      <ScrollSync>
        <div style={{ position: 'relative' }}>
          <CTable display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                max-width="100%"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="0">
                  <ReportsTableHeadCell width="30px" justifyContent="center" flex="30 0 auto"></ReportsTableHeadCell>
                  <ReportsTableHeadCell>{t('common:name')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.show_employee_id ? (
                    <ReportsTableHeadCell width="120px">{t('common:userID')}</ReportsTableHeadCell>
                  ) : null}
                  <ReportsTableHeadCell width="250px">{t('common:jobOrTask')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="200px">{t('common:clockIn/Out')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="150px">{`${t('common:paidBreaks')} (${t(
                    'common:mins'
                  )})`}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="150px">{`${t('common:unpaidBreaks')} (${t(
                    'common:mins'
                  )})`}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('common:duration')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.show_mileage ? (
                    <ReportsTableHeadCell width="140px">{t('common:miles/km')}</ReportsTableHeadCell>
                  ) : null}
                  <ReportsTableHeadCell width="120px">{t('reports:regularHrs')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('reports:regularPay')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.daily_overtime ? (
                    <ReportsTableHeadCell width="120px">{t('reports:OTHrs')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.daily_double_overtime ? (
                    <ReportsTableHeadCell width="120px">{t('reports:DOThrs')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.show_timesheet_notes && (
                    <ReportsTableHeadCell>{t('common:notes')}</ReportsTableHeadCell>
                  )}
                  {userStore?.userInfo?.roleId === roles.ADMIN || userStore?.permissions?.manageTimesheets ? (
                    <ReportsTableHeadCell width="80px"></ReportsTableHeadCell>
                  ) : null}
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>
            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {currentItems.length > 0 ? (
                  currentItems.map((item: any, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        {/* Total Data */}
                        {item?.isTotal ? (
                          <ReportsTableRow
                            background="primary.50"
                            minWidth="100%"
                            width="fit-content"
                            className="js-row-total"
                          >
                            <ReportsTableDataCell width="220px" fontWeight="semibold">
                              {isDistanceInMiles() ? t('reports:totalMileage') : t('reports:totalKilometers')}:{' '}
                              {item?.total_mileage ? formatDistanceUnit(+item?.total_mileage) : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="240px" fontWeight="semibold">
                              {isDistanceInMiles() ? t('reports:totalMileageCost') : t('reports:totalKilometersCost')}:{' '}
                              {item?.total_mileage_cost ? formatCurrency(item?.total_mileage_cost) : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="200px" fontWeight="semibold">
                              {t('reports:regularHours')}: {item?.regular_hours ? item?.regular_hours : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="225px" fontWeight="semibold">
                              {t('reports:overtimeHours')}:{' '}
                              {item?.daily_overtime_hours ? item?.daily_overtime_hours : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="260px" fontWeight="semibold">
                              {t('reports:doubleOvertimeHours')}:{' '}
                              {item?.double_overtime_hours ? item?.double_overtime_hours : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="200px" fontWeight="semibold">
                              {t('reports:totalHours')}: {item?.total_hours ? item?.total_hours : ''}
                            </ReportsTableDataCell>
                            <ReportsTableDataCell width="220px" fontWeight="semibold">
                              {t('reports:totalHoursCost')}: {item?.total_cost ? formatCurrency(+item?.total_cost) : ''}
                            </ReportsTableDataCell>
                          </ReportsTableRow>
                        ) : (
                          <>
                            {/* Timesheet Data */}
                            <ReportsTableRow key={item?.id} minWidth="100%" width="fit-content">
                              <ReportsTableDataCell width="30px" justifyContent="center" flex="30 0 auto">
                                <Checkbox
                                  isChecked={checkedTimesheet.includes(item?.id)}
                                  onChange={e => handleCheckboxChange(e, item?.id)}
                                  colorScheme="primary"
                                />
                              </ReportsTableDataCell>
                              <ReportsTableDataCell>
                                {index === 0 || (index > 0 && currentItems[index - 1].isTotal)
                                  ? item.first_name + ' ' + item.last_name
                                  : ''}
                              </ReportsTableDataCell>
                              {userStore?.companyInfo?.show_employee_id ? (
                                <ReportsTableDataCell width="120px">
                                  {index === 0 || (index > 0 && currentItems[index - 1].isTotal)
                                    ? item?.employee_id
                                    : ''}
                                </ReportsTableDataCell>
                              ) : null}
                              <ReportsTableDataCell width="250px">
                                <Box display="flex" flexDirection="column">
                                  {item?.job?.name || '- '}
                                  {item?.task?.name ? <Box>{'/' + item?.task?.name}</Box> : null}
                                </Box>
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="200px">
                                {item?.clock_in?.local_time ? formatYearMonthDayTime(item?.clock_in?.local_time) : '- '}
                                {' - '}
                                <br />
                                {item?.clock_out?.local_time ? formatYearMonthDayTime(item?.clock_out?.local_time) : ''}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="150px">
                                {convertSecondsToMinutesRoundDown(item?.paid_break_in_seconds)}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="150px">
                                {convertSecondsToMinutesRoundDown(item?.unpaid_break_in_seconds)}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.payrolls?.duration || '- '}
                              </ReportsTableDataCell>
                              {userStore?.companyInfo?.show_mileage ? (
                                <ReportsTableDataCell width="140px">
                                  {item?.mileage ? formatDistanceUnit(+item?.mileage) : '- '}
                                </ReportsTableDataCell>
                              ) : null}
                              <ReportsTableDataCell width="120px">
                                {item?.payrolls?.regular_time || '- '}
                              </ReportsTableDataCell>
                              <ReportsTableDataCell width="120px">
                                {item?.payrolls?.regular_cost || '- '}
                              </ReportsTableDataCell>
                              {userStore?.companyInfo?.daily_overtime ? (
                                <ReportsTableDataCell width="120px">
                                  {item?.payrolls?.overtime || '- '}
                                </ReportsTableDataCell>
                              ) : null}
                              {userStore?.companyInfo?.daily_double_overtime ? (
                                <ReportsTableDataCell width="120px">
                                  {item?.payrolls?.double_overtime || '- '}
                                </ReportsTableDataCell>
                              ) : null}

                              {userStore?.companyInfo?.show_timesheet_notes ? (
                                <ReportsTableDataCell>{item?.notes}</ReportsTableDataCell>
                              ) : null}
                              <EditActionButton timesheetId={item?.id} withoutTd={false} />
                            </ReportsTableRow>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <EmptyTableRow />
                )}
              </Tbody>
            </ScrollSyncPane>
          </CTable>
        </div>
      </ScrollSync>
      <ReactTablePagination
        pageCount={data ? Math.ceil(formattedData.length / +paginationQuery.per_page) : 1}
        onPageChange={handlePageChange}
        currentPage={paginationQuery.current_page - 1}
        totalItems={formattedData.length || 0}
        rowsPerPage={(paginationQuery.per_page as number) ?? 10}
        handlePageSizeChange={handlePageSizeChange}
      />
      {children}
    </div>
  );
};

export default ReportsByTaskTable;
